import { ChangeDetectorRef, DestroyRef, Directive, ElementRef, HostBinding, Input, OnInit, Renderer2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MutationObserverService, ResizeObserverService } from '@frontend/sports/common/core/utils/dom';
import { WINDOW } from '@frontend/vanilla/core';
import { distinctUntilChanged, filter, map } from 'rxjs';

export interface WhenOverflowedOptions {
    classNameToAdd?: string;
    resizeFont?: boolean;
    minFontSize?: number;
    listenToResize?: boolean;
    manualMutationCheck?: boolean;
    adjustRatio?: number;
}

@Directive({
    selector: '[msWhenOverflowed]',
    standalone: true,
})
export class WhenOverflowedDirective implements OnInit {
    @Input('msWhenOverflowed') options: WhenOverflowedOptions = {};

    @HostBinding('style.font-size.px') fontSize?: number;

    private defaultFont = 0;
    private readonly destroyRef = inject(DestroyRef);
    private readonly _window = inject(WINDOW);

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        private mutationObserver: MutationObserverService,
        private resizeObserver: ResizeObserverService,
        private changeDetectionRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        if (!this.options.manualMutationCheck && (this.options.classNameToAdd || this.options.resizeFont)) {
            this.initMutationObserver();
        }

        if (this.options.listenToResize) {
            this.initResizeObserver();
        }
    }

    private get element(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    forceRefresh(): void {
        this.recalculate();
    }

    private initResizeObserver(): void {
        this.resizeObserver

            .observe(this.elementRef)

            .pipe(
                map((entry: ResizeObserverEntry) => Math.round(entry.contentRect.width)),
                filter((w) => w > 0),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )

            .subscribe(() => this.recalculate());
    }

    private initMutationObserver(): void {
        const options = {
            characterData: true,
            childList: true,
            subtree: true,
        };

        this.mutationObserver
            .observe(this.element, options)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.recalculate());
    }

    private recalculate(): void {
        this.renderer.removeClass(this.element, this.options.classNameToAdd ?? '');
        this.fontSize = undefined;
        this.changeDetectionRef.detectChanges();

        if (this.options.resizeFont && !this.defaultFont) {
            const style = this._window.getComputedStyle(this.element);
            this.defaultFont = parseFloat(style.fontSize);
        }

        if (this.overflowRatio < 1) {
            if (this.options.resizeFont && this.options.minFontSize) {
                const newFont = +Math.max(
                    this.options.minFontSize,
                    Math.min(this.defaultFont, this.defaultFont * this.overflowRatio * (this.options.adjustRatio ?? 1)),
                ).toFixed(2);
                this.fontSize = newFont;
            }

            this.renderer.addClass(this.element, this.options.classNameToAdd ?? '');
            this.changeDetectionRef.detectChanges();
        }
    }

    private get overflowRatio(): number {
        return this.element.offsetWidth / this.element.scrollWidth;
    }
}
